import React, { Component } from 'react'
import leftIcon from '../../../../../assets/icons/arrow-left.png'
import rightIcon from '../../../../../assets/icons/arrow-right.png'
import { addResizeListener } from '../../../../../insfrastructure/utils/dom-utils'
import './styles.sass'

interface SlideImage {
  image: string;
  imageMobile?: string | null;
  left?: number;
}

interface SlideProps {
  passedSlides: (string | SlideImage)[];
}

interface State {
  slides: SlideImage[];
  currentSlide: number;
  sliderWidth: number;
  sliderHeight: number;
  sliderInterval: NodeJS.Timeout | null;
}

export class ItemSlider extends Component<SlideProps> {
  state: State = {
    slides: [],
    currentSlide: 1,
    sliderWidth: 0,
    sliderHeight: 0,
    sliderInterval: null
  }
  sliderRef = React.createRef<HTMLDivElement>()
  resizeCleanup: (() => void) | null = null

  componentDidMount() {
    if(!this.sliderRef.current) return;

    const sliderWidth = this.sliderRef.current.offsetWidth;
    if (sliderWidth === 0) return;

    const slidesCopy = this.props.passedSlides.map((x): SlideImage => {
      if (typeof x === 'object' && x !== null && 'image' in x) {
        return {
          image: x.image || '',
          imageMobile: x.imageMobile || null
        };
      }
      return {
        image: typeof x === 'string' ? x : '',
        imageMobile: null
      };
    });
  
    if (slidesCopy.length === 0) return;

    // Se houver apenas 1 slide, não precisa de clones
    const slides: SlideImage[] = slidesCopy.length === 1
      ? [{ ...slidesCopy[0], left: 0 }]
      : [
          { ...slidesCopy[slidesCopy.length - 1], left: 0 },
          ...slidesCopy.map((slide, index) => ({
            ...slide,
            left: sliderWidth * (index + 1)
          })),
          { ...slidesCopy[0], left: sliderWidth * (slidesCopy.length + 1) }
        ];

    this.setState({
      sliderWidth,
      slides
    }, () => {
      this.calculateSliderHeight();
      this.resizeCleanup = addResizeListener(this.handleResize, 250);
      
      if (slidesCopy.length > 1) {
        setTimeout(() => this.jumpToPosition(1), 100);
      }
    });
  }

  componentWillUnmount() {
    if (this.resizeCleanup) {
      this.resizeCleanup();
    }
    if (this.state.sliderInterval) {
      clearInterval(this.state.sliderInterval);
    }
  }

  handleResize = () => {
    if (!this.sliderRef.current) return;
    
    const newWidth = this.sliderRef.current.offsetWidth;
    if (newWidth === 0 || newWidth === this.state.sliderWidth) return;

    // Recalcular posições dos slides
    const slides = this.state.slides.map((slide, index) => ({
      ...slide,
      left: this.props.passedSlides.length === 1 
        ? 0 
        : (newWidth * index) - (newWidth * this.state.currentSlide)
    }));

    this.setState({
      sliderWidth: newWidth,
      slides
    }, () => {
      this.calculateSliderHeight();
      if (this.props.passedSlides.length > 1) {
        this.jumpToPosition(this.state.currentSlide);
      }
    });
  }

  calculateSliderHeight = () => {
    if (this.props.passedSlides.length === 0 || this.state.sliderWidth === 0) {
      return;
    }

    const firstSlide = this.props.passedSlides[0];
    const isMobile = window.innerWidth <= 768;
    const maxDesktopWidth = 1200;
    
    let imageSrc = '';
    if (typeof firstSlide === 'object' && firstSlide !== null && 'image' in firstSlide) {
      imageSrc = isMobile && firstSlide.imageMobile 
        ? firstSlide.imageMobile 
        : (firstSlide.image || '');
    } else if (typeof firstSlide === 'string') {
      imageSrc = firstSlide;
    }

    if (!imageSrc) return;
    
    const img = new Image();
    img.onload = () => {
      if (!this.sliderRef.current || this.state.sliderWidth === 0) return;
      const aspectRatio = img.height / img.width;
      const effectiveWidth = isMobile
        ? this.state.sliderWidth
        : Math.min(this.state.sliderWidth, maxDesktopWidth);
      this.setState({ sliderHeight: effectiveWidth * aspectRatio });
    };
    img.onerror = () => {
      const defaultAspectRatio = 428 / 1600;
      const effectiveWidth = isMobile
        ? this.state.sliderWidth
        : Math.min(this.state.sliderWidth, maxDesktopWidth);
      this.setState({ sliderHeight: effectiveWidth * defaultAspectRatio });
    };
    img.src = imageSrc;
  }

  onBtnClick = (orientation: 'next' | 'before') => {
    if (this.props.passedSlides.length <= 1) return;

    const slides = this.state.slides.map(slide => ({
      ...slide,
      left: (slide.left ?? 0) + (orientation === 'next' ? -this.state.sliderWidth : this.state.sliderWidth)
    }));

    this.setState({
      slides,
      currentSlide: orientation === 'next' ? this.state.currentSlide + 1 : this.state.currentSlide - 1
    });
  }

  jumpToPosition = (currentSlide: number) => {
    if (this.props.passedSlides.length <= 1) return;

    const slides = this.state.slides.map((slide, index) => ({
      ...slide,
      left: (this.state.sliderWidth * index) - (this.state.sliderWidth * currentSlide)
    }));

    this.setState({
      slides,
      currentSlide
    });
  }

  render() {
    const containerStyle = {
      height: this.state.sliderHeight > 0 ? `${this.state.sliderHeight}px` : '300px'
    };

    return (
      <div className='slider-container'>
        <div ref={this.sliderRef} className='item-slider-container noselect' style={containerStyle}>
          {this.state.slides.length > 1 &&
          <>
            {this.state.currentSlide > 1 &&
            <button 
              className='previous jump-btn' 
              onClick={() => this.onBtnClick('before')}
              aria-label="Slide anterior"
            >
              <img src={leftIcon} alt="Anterior" />
            </button>}
            {this.state.currentSlide < this.props.passedSlides.length &&
              <button 
                className='next jump-btn' 
                onClick={() => this.onBtnClick('next')}
                aria-label="Próximo slide"
              >
              <img src={rightIcon} alt="Próximo" />
            </button>}
          </>}
          <div className='slides'>
            {
              this.state.slides.map((slide, index) => 
                <div key={'item_slide_'+index} className='slide' style={{width: this.state.sliderWidth, left: slide.left ?? 0}}>
                  {slide.imageMobile && typeof slide.imageMobile === 'string' ? (
                    <>
                      <img className='background background-desktop' src={slide.image} alt="Banner desktop" />
                      <img className='background background-mobile' src={slide.imageMobile} alt="Banner mobile" />
                    </>
                  ) : (
                    <img className='background' src={slide.image} alt="Banner" />
                  )}
                </div>  
              )
            }
          </div>
        </div>
      </div>
    )
  }
}